import { z } from "zod";
import { optNum } from "./zodNum";
import { isoDate } from "./dates";

export const SPACE_CATEGORIES = ["LETTABLE", "COMMON", "TECHNICAL", "NON_LETTABLE"] as const;
export type SpaceCategory = (typeof SPACE_CATEGORIES)[number];
export const AREA_BASES = ["NET", "GROSS", "LETTABLE"] as const;
export const COMMON_SCOPES = ["PROPERTY", "BUILDING", "FLOOR"] as const;

/** A helyiség állapota egy adott napon (számított, nem tárolt). */
export const OCCUPANCY_STATUSES = ["LET", "VACANT", "OWN", "COMMON", "TECHNICAL", "NON_LETTABLE"] as const;
export type OccupancyStatus = (typeof OCCUPANCY_STATUSES)[number];

export const CATEGORY_LABEL: Record<SpaceCategory, string> = {
  LETTABLE: "Bérbe adható",
  COMMON: "Közös terület",
  TECHNICAL: "Gépészeti / technikai",
  NON_LETTABLE: "Nem bérbe adható",
};

export const STATUS_LABEL: Record<OccupancyStatus, string> = {
  LET: "Bérbe adva",
  VACANT: "Üres",
  OWN: "Saját használat",
  COMMON: "Közös",
  TECHNICAL: "Gépészet",
  NON_LETTABLE: "Nem bérbe adható",
};

export const AREA_BASIS_LABEL: Record<(typeof AREA_BASES)[number], string> = {
  NET: "Nettó",
  GROSS: "Bruttó",
  LETTABLE: "Bérbe adható",
};

export const COMMON_SCOPE_LABEL: Record<(typeof COMMON_SCOPES)[number], string> = {
  PROPERTY: "Az egész ingatlan",
  BUILDING: "Az épület bérlői",
  FLOOR: "A szint bérlői",
};

/** Új szervezet alapértelmezett helyiségtípusai (a spec típuslistája alapján, bővíthető). */
export const DEFAULT_SPACE_TYPES: { key: string; label: string; color: string; defaultCategory: SpaceCategory }[] = [
  { key: "office", label: "Iroda", color: "#4F7DF3", defaultCategory: "LETTABLE" },
  { key: "storage", label: "Raktár", color: "#C98A3B", defaultCategory: "LETTABLE" },
  { key: "retail", label: "Üzlet", color: "#E0679A", defaultCategory: "LETTABLE" },
  { key: "workshop", label: "Üzem", color: "#8B6FD9", defaultCategory: "LETTABLE" },
  { key: "parking", label: "Parkoló", color: "#5B8FA8", defaultCategory: "LETTABLE" },
  { key: "outdoor", label: "Szabadtéri terület", color: "#4FAE7B", defaultCategory: "LETTABLE" },
  { key: "meeting", label: "Tárgyaló", color: "#E08A4F", defaultCategory: "COMMON" },
  { key: "reception", label: "Recepció, fogadótér", color: "#B58FD6", defaultCategory: "COMMON" },
  { key: "circulation", label: "Közlekedő, lépcsőház, lift", color: "#A3A9B8", defaultCategory: "COMMON" },
  { key: "restroom", label: "Vizesblokk", color: "#7FB6C9", defaultCategory: "COMMON" },
  { key: "kitchen", label: "Teakonyha", color: "#D9A95B", defaultCategory: "COMMON" },
  { key: "mechanical", label: "Gépészeti helyiség", color: "#7C8391", defaultCategory: "TECHNICAL" },
];

const decimalArea = z.coerce.number({ invalid_type_error: "Számot adj meg" }).min(0, "Nem lehet negatív").max(10_000_000);

export const spaceFieldsSchema = z.object({
  code: z.string().trim().min(1, "Kötelező").max(40),
  name: z.string().trim().max(120).nullable().optional(),
  buildingId: z.string().uuid(),
  floorId: z.string().uuid().nullable().optional(),
  spaceTypeId: z.string().uuid({ message: "Válassz típust" }),
  area: decimalArea,
  areaBasis: z.enum(AREA_BASES).default("NET"),
  category: z.enum(SPACE_CATEGORIES),
  commonScope: z.enum(COMMON_SCOPES).default("PROPERTY"),
  excludeFromServiceCharge: z.boolean().default(false),
  parkingCount: optNum(z.coerce.number().int().min(0)),
  note: z.string().trim().max(1000).nullable().optional(),
});
export type SpaceFields = z.infer<typeof spaceFieldsSchema>;

const eventBase = {
  effectiveDate: isoDate,
  /** Kötelező, ha a változás az ingatlan teljes területét módosítja (kontroll szabály). */
  reason: z.string().trim().max(1000).nullable().optional(),
};

export const createSpacesSchema = z.object({
  ...eventBase,
  eventType: z.enum(["INITIAL", "EXTENSION"]).default("INITIAL"),
  spaces: z.array(spaceFieldsSchema).min(1),
});

export const reviseSpaceSchema = z.object({ ...eventBase, fields: spaceFieldsSchema.partial() });

export const splitSpaceSchema = z.object({
  ...eventBase,
  spaceId: z.string().uuid(),
  parts: z.array(spaceFieldsSchema.partial().extend({ code: z.string().trim().min(1).max(40), area: decimalArea })).min(2, "Legalább két részre kell bontani"),
});

export const mergeSpacesSchema = z.object({
  ...eventBase,
  spaceIds: z.array(z.string().uuid()).min(2, "Legalább két helyiség kell"),
  result: spaceFieldsSchema.partial().extend({ code: z.string().trim().min(1).max(40) }),
});

export const retireSpacesSchema = z.object({
  ...eventBase,
  spaceIds: z.array(z.string().uuid()).min(1),
  reason: z.string().trim().min(3, "Írd le röviden, miért szűnik meg").max(1000),
});

export const newTenantSchema = z.object({
  kind: z.enum(["COMPANY", "PERSON"]).default("COMPANY"),
  name: z.string().trim().min(2).max(200),
  shortName: z.string().trim().max(60).nullable().optional(),
  taxNumber: z.string().trim().max(30).nullable().optional(),
  email: z.string().trim().toLowerCase().email().nullable().optional().or(z.literal("")),
  phone: z.string().trim().max(40).nullable().optional(),
});

export const letSpacesSchema = z
  .object({
    effectiveDate: isoDate,
    /** Az első nap, amikor már NEM bérli (félig nyitott); null = határozatlan. */
    endDate: isoDate.nullable().optional(),
    spaceIds: z.array(z.string().uuid()).default([]),
    tenantId: z.string().uuid().optional(),
    newTenant: newTenantSchema.optional(),
    /** Meglévő bérleményhez / szerződéshez csatolás új szerződés helyett. */
    leaseId: z.string().uuid().optional(),
    unitKind: z.enum(["PHYSICAL", "VIRTUAL", "SUBLET_IN_OWNER_SPACE"]).default("PHYSICAL"),
    unitName: z.string().trim().max(120).nullable().optional(),
  })
  .refine((v) => v.tenantId || v.newTenant || v.leaseId, { message: "Válassz bérlőt", path: ["tenantId"] })
  .refine((v) => v.unitKind !== "PHYSICAL" || v.spaceIds.length > 0, { message: "Válassz legalább egy helyiséget", path: ["spaceIds"] });

export const vacateSpacesSchema = z.object({ effectiveDate: isoDate, spaceIds: z.array(z.string().uuid()).min(1) });

export const ownerOccupancySchema = z.object({
  effectiveDate: isoDate,
  spaceIds: z.array(z.string().uuid()).min(1),
  ownerOccupied: z.boolean(),
});

export const areaTargetsSchema = z.object({
  effectiveDate: isoDate,
  targets: z.array(z.object({ spaceTypeId: z.string().uuid(), area: decimalArea.nullable() })),
});

export const buildingSchema = z.object({ name: z.string().trim().min(1).max(120) });
export const floorSchema = z.object({ name: z.string().trim().min(1).max(60), level: z.coerce.number().int().min(-10).max(200) });
export const spaceTypeSchema = z.object({
  label: z.string().trim().min(1).max(80),
  color: z.string().regex(/^#[0-9a-fA-F]{6}$/),
  defaultCategory: z.enum(SPACE_CATEGORIES),
});
